/**
 * Pure math helpers for the command-buffer slice: camera math, transform
 * component pivots (vertex/edge/face), object-space pivots, scaling and
 * rotation primitives, selection transform component building.
 *
 * Nothing in this module touches the store — every function is pure.
 * `transformTargets.ts` wraps these into the AppState-producing patch
 * builder; `index.ts` composes everything into the actions slice.
 */

import * as THREE from "three/webgpu";
import { isFaceMode } from "../../modeling/editSelection.ts";
import {
  computePivot,
  rotatePointFromPivot,
  scalePointFromPivot,
} from "../../modeling/pivot.ts";
import { edgeVertices } from "../../modeling/topology.ts";
import type { PolygonTopology, SceneObject } from "../../modeling/types.ts";
import { worldTransformForSceneObject } from "../collectionGraph.ts";
import type { ObjectSelectionTarget } from "../objectSelection.ts";
import type {
  AppState,
  AxisConstraint,
  Collection,
  UndoableSnapshot,
} from "../storeTypes.ts";

// ---------------------------------------------------------------------------
// Camera-agnostic visible height — works for both perspective & orthographic.
// ---------------------------------------------------------------------------

/** World-space visible height at a given distance from the camera. */
export function visibleHeight(camera: THREE.Camera, dist: number): number {
  if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
    const ortho = camera as THREE.OrthographicCamera;
    return ortho.top - ortho.bottom;
  }
  const persp = camera as THREE.PerspectiveCamera;
  const vFov = (persp.fov * Math.PI) / 180;
  return 2 * dist * Math.tan(vFov / 2);
}

// ---------------------------------------------------------------------------
// Snapshot transform targets — what the command buffer knows how to move
// ---------------------------------------------------------------------------

export type SnapshotTransformTarget =
  | {
      kind: "scene";
      id: string;
      position: [number, number, number];
      rotationEuler: [number, number, number];
      scale: [number, number, number];
    }
  | {
      kind: "blend";
      id: string;
      position: [number, number, number];
      rotationQuat: [number, number, number, number];
      scale: [number, number, number];
    }
  | {
      kind: "collection";
      id: string;
      position: [number, number, number];
      rotationQuat: [number, number, number, number];
      scale: [number, number, number];
    };

export type EditTransformComponent = {
  pivot: [number, number, number];
  vertices: number[];
};

export function collectSnapshotTransformTargets(
  snap: UndoableSnapshot,
  selectedTargets: readonly ObjectSelectionTarget[],
): SnapshotTransformTarget[] {
  const targets: SnapshotTransformTarget[] = [];
  for (const target of selectedTargets) {
    if (target.kind === "blendEntry") {
      const gt = snap.groupTransforms.get(target.id);
      if (!gt) continue;
      targets.push({
        kind: "blend",
        id: target.id,
        position: [...gt.position],
        rotationQuat: [...gt.rotation],
        scale: [...gt.scale],
      });
      continue;
    }

    if (target.kind === "collection") {
      const collection = snap.collections.find(
        (entry) => entry.id === target.id,
      );
      if (!collection) continue;
      targets.push({
        kind: "collection",
        id: target.id,
        position: [...collection.transform.position],
        rotationQuat: [...collection.transform.rotation],
        scale: [...collection.transform.scale],
      });
      continue;
    }

    const so = snap.sceneObjects.find((entry) => entry.id === target.id);
    if (!so) continue;
    targets.push({
      kind: "scene",
      id: so.id,
      position: [...so.position],
      rotationEuler: [...so.rotation],
      scale: [...so.scale],
    });
  }
  return targets;
}

export function computeObjectPivot(
  targets: ReadonlyArray<SnapshotTransformTarget>,
  reference: AppState["pivotReference"],
  cursorWorld: readonly [number, number, number],
): [number, number, number] {
  return computePivot(
    targets.map((target) => target.position),
    reference,
    cursorWorld,
  );
}

export function computeSnapAlignedObjectRotation(
  target: SnapshotTransformTarget,
  worldNormal: readonly [number, number, number] | null | undefined,
):
  | { rotationEuler: [number, number, number] }
  | { rotationQuat: [number, number, number, number] }
  | null {
  if (!worldNormal) return null;
  const normal = new THREE.Vector3(
    worldNormal[0],
    worldNormal[1],
    worldNormal[2],
  );
  if (normal.lengthSq() < 1e-12) return null;
  normal.normalize();

  const currentQuat =
    target.kind === "scene"
      ? new THREE.Quaternion().setFromEuler(
          new THREE.Euler(...target.rotationEuler),
        )
      : new THREE.Quaternion(...target.rotationQuat).normalize();
  const currentNormal = new THREE.Vector3(0, 0, 1)
    .applyQuaternion(currentQuat)
    .normalize();
  const deltaQuat = new THREE.Quaternion().setFromUnitVectors(
    currentNormal,
    normal,
  );
  const nextQuat = deltaQuat.multiply(currentQuat).normalize();

  if (target.kind === "scene") {
    const currentEuler = new THREE.Euler(...target.rotationEuler);
    const nextEuler = new THREE.Euler().setFromQuaternion(
      nextQuat,
      currentEuler.order,
    );
    return { rotationEuler: [nextEuler.x, nextEuler.y, nextEuler.z] };
  }

  return {
    rotationQuat: [nextQuat.x, nextQuat.y, nextQuat.z, nextQuat.w],
  };
}

/**
 * In edit mode, vertex positions live in object-local space while the
 * 3D cursor is world-space. When `pivotReference === "cursor"`, convert
 * the cursor into the object's local frame once so the pivot math stays
 * consistent with the surrounding vertex data.
 *
 * `parentCollection` is the Collection the object is a member of, or `null`
 * if the object is at the scene root. When present, the object's world
 * frame composes collection.transform ∘ obj local, so the inverse must
 * back out both to land in child-local vertex space.
 */
export function cursorInObjectLocal(
  obj: SceneObject,
  parentCollection: Collection | null,
  cursorWorld: readonly [number, number, number],
): [number, number, number] {
  const w = worldTransformForSceneObject(obj, parentCollection);
  const m = new THREE.Matrix4()
    .compose(
      new THREE.Vector3(w.position[0], w.position[1], w.position[2]),
      new THREE.Quaternion().setFromEuler(
        new THREE.Euler(w.rotation[0], w.rotation[1], w.rotation[2]),
      ),
      new THREE.Vector3(w.scale[0], w.scale[1], w.scale[2]),
    )
    .invert();
  const v = new THREE.Vector3(
    cursorWorld[0],
    cursorWorld[1],
    cursorWorld[2],
  ).applyMatrix4(m);
  return [v.x, v.y, v.z];
}

// ---------------------------------------------------------------------------
// Component-pivot construction for edit-mode transforms
// ---------------------------------------------------------------------------

export function findNearestComponentPivot(
  vertexIndex: number,
  srcVerts: Float32Array,
  components: ReadonlyArray<EditTransformComponent>,
): [number, number, number] | null {
  let best: [number, number, number] | null = null;
  let bestDist2 = Infinity;
  const vx = srcVerts[vertexIndex * 3];
  const vy = srcVerts[vertexIndex * 3 + 1];
  const vz = srcVerts[vertexIndex * 3 + 2];
  for (const component of components) {
    const dx = vx - component.pivot[0];
    const dy = vy - component.pivot[1];
    const dz = vz - component.pivot[2];
    const dist2 = dx * dx + dy * dy + dz * dz;
    if (dist2 < bestDist2) {
      bestDist2 = dist2;
      best = component.pivot;
    }
  }
  return best;
}

/**
 * Pre-resolve the nearest component pivot for each proportional-edit
 * vertex once at command-enter, so the per-frame
 * `applyEditComponentTransform` doesn't repeat the O(components) search
 * for every influenced vertex on every pointermove. The proportional
 * vertex set and component pivots are command-stable (snapshot-based),
 * so this lookup is correct for the duration of the drag.
 */
export function buildProportionalPivotLookup(
  srcVerts: Float32Array,
  components: ReadonlyArray<EditTransformComponent>,
  proportionalWeights: ReadonlyMap<number, number>,
): Map<number, [number, number, number]> {
  const out = new Map<number, [number, number, number]>();
  if (components.length === 0) return out;
  for (const vi of proportionalWeights.keys()) {
    const pivot = findNearestComponentPivot(vi, srcVerts, components);
    if (pivot) out.set(vi, pivot);
  }
  return out;
}

/**
 * Apply an edit-mode transform to every vertex in every component (hard
 * weight 1) plus any `proportionalWeights` vertices (falloff weight).
 * Proportional vertices are transformed against whichever component
 * pivot is nearest — matches Blender's per-component falloff behaviour.
 *
 * `proportionalPivots` is an optional per-vertex pivot lookup; when
 * supplied (the live-drag path passes the cache built at command-enter),
 * the inner nearest-pivot search is skipped entirely. When omitted, we
 * fall back to scanning components per influenced vertex — used by code
 * paths that don't have a command-scoped cache.
 */
export function applyEditComponentTransform(
  srcVerts: Float32Array,
  newVerts: Float32Array,
  components: ReadonlyArray<EditTransformComponent>,
  proportionalWeights: ReadonlyMap<number, number> | null,
  transformPoint: (
    point: readonly [number, number, number],
    pivot: readonly [number, number, number],
    weight: number,
  ) => [number, number, number],
  proportionalPivots?: ReadonlyMap<number, [number, number, number]> | null,
): void {
  for (const component of components) {
    for (const vi of component.vertices) {
      const point: [number, number, number] = [
        srcVerts[vi * 3],
        srcVerts[vi * 3 + 1],
        srcVerts[vi * 3 + 2],
      ];
      const next = transformPoint(point, component.pivot, 1);
      newVerts[vi * 3] = next[0];
      newVerts[vi * 3 + 1] = next[1];
      newVerts[vi * 3 + 2] = next[2];
    }
  }

  if (!proportionalWeights || components.length === 0) return;

  for (const [vi, weight] of proportionalWeights) {
    const pivot =
      proportionalPivots?.get(vi) ??
      findNearestComponentPivot(vi, srcVerts, components);
    if (!pivot) continue;
    const point: [number, number, number] = [
      srcVerts[vi * 3],
      srcVerts[vi * 3 + 1],
      srcVerts[vi * 3 + 2],
    ];
    const next = transformPoint(point, pivot, weight);
    newVerts[vi * 3] = next[0];
    newVerts[vi * 3 + 1] = next[1];
    newVerts[vi * 3 + 2] = next[2];
  }
}

function buildVertexComponentPivots(
  srcVerts: Float32Array,
  selectedVertices: ReadonlySet<number>,
): EditTransformComponent[] {
  const components: EditTransformComponent[] = [];
  for (const vi of selectedVertices) {
    components.push({
      vertices: [vi],
      pivot: [srcVerts[vi * 3], srcVerts[vi * 3 + 1], srcVerts[vi * 3 + 2]],
    });
  }
  return components;
}

function buildEdgeComponentPivots(
  srcVerts: Float32Array,
  selectedEdges: ReadonlySet<number>,
  topoEdges: Uint32Array,
): EditTransformComponent[] {
  if (selectedEdges.size === 0) return [];

  const edgeIndices = [...selectedEdges];
  const edges = edgeIndices.map((ei) => edgeVertices(topoEdges, ei));
  const vertToEdges = new Map<number, number[]>();
  edges.forEach(([a, b], i) => {
    const aEdges = vertToEdges.get(a) ?? [];
    aEdges.push(i);
    vertToEdges.set(a, aEdges);
    const bEdges = vertToEdges.get(b) ?? [];
    bEdges.push(i);
    vertToEdges.set(b, bEdges);
  });

  const visited = new Set<number>();
  const components: EditTransformComponent[] = [];
  for (let edgeIndex = 0; edgeIndex < edges.length; edgeIndex++) {
    if (visited.has(edgeIndex)) continue;
    const stack = [edgeIndex];
    const componentVerts = new Set<number>();
    while (stack.length > 0) {
      const current = stack.pop()!;
      if (visited.has(current)) continue;
      visited.add(current);
      const [a, b] = edges[current];
      componentVerts.add(a);
      componentVerts.add(b);
      for (const next of vertToEdges.get(a) ?? [])
        if (!visited.has(next)) stack.push(next);
      for (const next of vertToEdges.get(b) ?? [])
        if (!visited.has(next)) stack.push(next);
    }

    const vertices = [...componentVerts];
    components.push({
      vertices,
      pivot: computePivot(
        vertices.map(
          (vi) =>
            [srcVerts[vi * 3], srcVerts[vi * 3 + 1], srcVerts[vi * 3 + 2]] as [
              number,
              number,
              number,
            ],
        ),
        "median",
      ),
    });
  }

  return components;
}

function buildFaceComponentPivots(
  srcVerts: Float32Array,
  topology: PolygonTopology,
  selectedFaces: ReadonlySet<number>,
): EditTransformComponent[] {
  if (selectedFaces.size === 0) return [];

  const vertToFaces = new Map<number, number[]>();
  for (const faceIndex of selectedFaces) {
    const start = topology.offsets[faceIndex];
    const end = topology.offsets[faceIndex + 1];
    for (let i = start; i < end; i++) {
      const vi = topology.corners[i];
      const faces = vertToFaces.get(vi) ?? [];
      faces.push(faceIndex);
      vertToFaces.set(vi, faces);
    }
  }

  const visited = new Set<number>();
  const components: EditTransformComponent[] = [];
  for (const faceIndex of selectedFaces) {
    if (visited.has(faceIndex)) continue;
    const stack = [faceIndex];
    const componentVerts = new Set<number>();
    while (stack.length > 0) {
      const current = stack.pop()!;
      if (visited.has(current)) continue;
      visited.add(current);
      const start = topology.offsets[current];
      const end = topology.offsets[current + 1];
      for (let i = start; i < end; i++) {
        const vi = topology.corners[i];
        componentVerts.add(vi);
        for (const nextFace of vertToFaces.get(vi) ?? []) {
          if (!visited.has(nextFace)) stack.push(nextFace);
        }
      }
    }

    const vertices = [...componentVerts];
    components.push({
      vertices,
      pivot: computePivot(
        vertices.map(
          (vi) =>
            [srcVerts[vi * 3], srcVerts[vi * 3 + 1], srcVerts[vi * 3 + 2]] as [
              number,
              number,
              number,
            ],
        ),
        "median",
      ),
    });
  }

  return components;
}

export function buildEditTransformComponents(
  srcVerts: Float32Array,
  topology: PolygonTopology | null,
  editMode: NonNullable<AppState["editMode"]>,
): EditTransformComponent[] {
  if (topology && isFaceMode(editMode)) {
    return buildFaceComponentPivots(srcVerts, topology, editMode.selectedFaces);
  }
  if (
    editMode.granularity === "edge" &&
    topology &&
    editMode.selectedEdges.size > 0
  ) {
    return buildEdgeComponentPivots(
      srcVerts,
      editMode.selectedEdges,
      topology.edges,
    );
  }
  return buildVertexComponentPivots(srcVerts, editMode.selectedVertices);
}

// ---------------------------------------------------------------------------
// Object-level transform math — scale / rotate / orient
// ---------------------------------------------------------------------------

export function computeScaledObjectPosition(
  position: readonly [number, number, number],
  pivot: readonly [number, number, number],
  factor: number,
  axis: AxisConstraint,
  normalVector: [number, number, number] | null,
): [number, number, number] {
  if (axis === "normal" && normalVector) {
    return scalePointFromPivot(position, pivot, factor, null, normalVector);
  }
  return scalePointFromPivot(
    position,
    pivot,
    factor,
    axis && axis !== "normal" ? axis : null,
  );
}

export function computeRotatedObjectPosition(
  position: readonly [number, number, number],
  pivot: readonly [number, number, number],
  axis: readonly [number, number, number],
  angle: number,
): [number, number, number] {
  return rotatePointFromPivot(position, pivot, axis, angle);
}

export function computeOrientedObjectScale(
  oldScale: readonly [number, number, number],
  factor: number,
  axis: AxisConstraint,
  normalVector: [number, number, number] | null,
  rotationQuat: THREE.Quaternion,
): [number, number, number] {
  if (axis === "normal" && normalVector) {
    const nv = new THREE.Vector3(...normalVector);
    const localDir = nv.applyQuaternion(rotationQuat.clone().invert());
    const dx = localDir.x;
    const dy = localDir.y;
    const dz = localDir.z;
    return [
      oldScale[0] * (1 + (factor - 1) * dx * dx),
      oldScale[1] * (1 + (factor - 1) * dy * dy),
      oldScale[2] * (1 + (factor - 1) * dz * dz),
    ];
  }
  if (axis && axis !== "normal") {
    return [
      oldScale[0] * (axis === "x" ? factor : 1),
      oldScale[1] * (axis === "y" ? factor : 1),
      oldScale[2] * (axis === "z" ? factor : 1),
    ];
  }
  return [oldScale[0] * factor, oldScale[1] * factor, oldScale[2] * factor];
}
